import {
  buildActionEconomyRequest,
  normalizeCombatActionRequest
} from './CombatActionRequestNormalizer';

const normalizeTargetIds = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === 'string' ? entry : entry?.id || null))
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value ? [value] : [];
  }

  if (value && typeof value === 'object' && value.id) {
    return [value.id];
  }

  return [];
};

export const resolveCombatTargets = ({ targetIds = [], tokens = [] } = {}) => {
  const normalizedTargetIds = normalizeTargetIds(targetIds);
  const availableTokens = Array.isArray(tokens) ? tokens : [];
  const targets = [];
  const unresolvedTargetIds = [];

  normalizedTargetIds.forEach((targetId) => {
    const target = availableTokens.find((token) => token?.id === targetId);
    if (target) {
      targets.push(target);
      return;
    }

    unresolvedTargetIds.push(targetId);
  });

  return {
    targetIds: normalizedTargetIds,
    targets,
    unresolvedTargetIds
  };
};

export const buildCombatExecutionState = ({
  tokens = [],
  gridSize,
  gridType,
  feetPerCell,
  extraCombatState = {}
} = {}) => ({
  tokens: Array.isArray(tokens) ? tokens : [],
  gridSize,
  gridType,
  feetPerCell,
  modifiers: {},
  readiedActions: {},
  ...extraCombatState
});

export const buildCombatExecutionBundle = (requestLike, options = {}) => {
  const baseRequest = normalizeCombatActionRequest(requestLike, options);
  const attacker = options.attacker || baseRequest.attacker || null;
  const { targetIds, targets, unresolvedTargetIds } = resolveCombatTargets({
    targetIds: options.targetIds ?? baseRequest.targetIds,
    tokens: options.tokens
  });
  const baseActionData = {
    ...baseRequest.actionData,
    ...(options.actionDataOverrides || {})
  };
  const actionData = typeof options.enrichActionData === 'function'
    ? (options.enrichActionData(baseActionData, baseRequest) || baseActionData)
    : baseActionData;
  const request = normalizeCombatActionRequest({
    attacker,
    attackerId: baseRequest.attackerId,
    actionData,
    targetIds,
    gmOverride: baseRequest.gmOverride,
    metadata: baseRequest.metadata
  });
  const processorRequest = buildActionEconomyRequest(request, {
    attacker,
    targets,
    actionType: options.actionType || request.engineActionType,
    movementCost: options.movementCost ?? actionData.movementCost ?? 0
  });

  if (options.actionEconomyConsumed || processorRequest.actionData?.__actionEconomyConsumed) {
    processorRequest.__actionEconomyConsumed = true;
    processorRequest.actionData = {
      ...processorRequest.actionData,
      __actionEconomyConsumed: true
    };
  }

  return {
    request,
    processorRequest,
    combatState: buildCombatExecutionState({
      tokens: options.tokens,
      gridSize: options.gridSize,
      gridType: options.gridType,
      feetPerCell: options.feetPerCell,
      extraCombatState: options.extraCombatState
    }),
    targets,
    targetIds,
    unresolvedTargetIds
  };
};

export const extractPrimaryProcessorOutcome = (processorResult) => (
  processorResult?.results?.[0]
  || processorResult?.result
  || null
);

const CombatExecutionRequestBuilder = {
  resolveCombatTargets,
  buildCombatExecutionState,
  buildCombatExecutionBundle,
  extractPrimaryProcessorOutcome
};

export default CombatExecutionRequestBuilder;
